import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Copy, Check, Mail } from "lucide-react";
import { MaskLine } from "./Reveal";
import { EMAIL } from "../../content";

const EmailPill = ({ t }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
    } catch (e) {
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href={`mailto:${EMAIL}`} data-testid="hero-email-link" className="btn-gold group">
        <Mail className="h-4 w-4" />
        <span>{EMAIL}</span>
        <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-45" />
      </a>
      <button onClick={copy} data-testid="copy-email-button" className="btn-ghost">
        {copied ? <Check className="h-4 w-4 text-[var(--gold)]" /> : <Copy className="h-4 w-4" />}
        <span>{copied ? t.hero.copied : t.hero.copy}</span>
      </button>
    </div>
  );
};

const HeroVisual = ({ y }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div style={{ y }} className="relative mx-auto w-full max-w-[440px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative aspect-[3/4] overflow-hidden rounded-t-full border border-[#d4af6a]/40 shadow-[0_40px_120px_-30px_rgba(212,175,106,0.35)]"
        data-testid="hero-visual"
      >
        <motion.img
          src="/img/cdmx.jpg"
          alt="Ciudad de México"
          initial={{ scale: 1.25 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover grayscale-[35%] sepia-[15%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1420]/80 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-[#f4f1ea]/90">
          <span>19.40° N</span>
          <span>99.15° W</span>
        </div>
      </motion.div>
      <div className="spin-badge absolute -left-6 top-10 hidden h-28 w-28 sm:block" aria-hidden="true">
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <defs><path id="c" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <text fontSize="8" letterSpacing="2" fill="var(--gold)" fontFamily="IBM Plex Mono, monospace">
            <textPath href="#c">S&amp;LC · PERSONNEL · MANAGEMENT ·</textPath>
          </text>
        </svg>
      </div>
    </motion.div>
  );
};

export const Hero = ({ t, lang }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="grain relative overflow-hidden bg-[var(--ink)] pb-20 pt-32 text-[var(--ivory)] lg:min-h-screen lg:pt-40">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full bg-[#d4af6a]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ y: yText }} className="lg:col-span-7" key={lang}>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--gold)]">
            <span className="h-px w-10 bg-[var(--gold)]" />{t.hero.eyebrow}
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[15vw] font-light leading-[0.92] tracking-[-0.03em] sm:text-7xl lg:text-[8.2rem]">
            {t.hero.lines.map((l, i) => (
              <MaskLine key={l} delay={0.15 + i * 0.14} className={i === 1 ? "italic text-[var(--gold)]" : ""}>{l}</MaskLine>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8 }} className="mt-10 max-w-xl space-y-8">
            <p data-testid="hero-subtitle" className="text-base leading-relaxed text-[#f4f1ea]/75 md:text-lg">{t.hero.sub}</p>
            <EmailPill t={t} />
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pt-6">
          <HeroVisual y={yImg} />
        </div>
      </div>
      <div className="relative mx-auto mt-16 hidden max-w-7xl items-center gap-3 px-10 font-mono text-[11px] uppercase tracking-[0.3em] text-[#f4f1ea]/60 lg:flex">
        <span className="scroll-line" />{t.hero.scroll}
      </div>
    </section>
  );
};
